import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Download, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DISMISSED_KEY = "emprenovus_pwa_install_dismissed_until";
const DISMISS_DAYS = 14;

function isStandalone() {
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isIOS() {
    return /iPhone|iPad|iPod/i.test(window.navigator.userAgent || "");
}

/**
 * Lightweight "Add to Home Screen" prompt.
 *
 * - On Chrome/Edge (Android/desktop) we intercept the `beforeinstallprompt`
 *   event and surface a custom install button.
 * - On iOS Safari (which doesn't fire that event) we show a brief
 *   instruction toast on mobile only.
 *
 * The banner is auto-hidden once the app is installed, once the user
 * dismisses it (snoozed for 14 days), or whenever the page is already
 * running in standalone display mode.
 */
export default function PWAInstallPrompt() {
    const { user } = useAuth();
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [ios, setIos] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;
        const snoozedUntil = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
        if (snoozedUntil && Date.now() < snoozedUntil) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
            setVisible(true);
        };
        const onInstalled = () => {
            setVisible(false);
            setDeferred(null);
            localStorage.removeItem(DISMISSED_KEY);
        };

        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);

        // iOS Safari path — no install event exists, so just show the hint
        // once and only on mobile, snoozed alongside the regular prompt.
        if (isIOS() && /Mobile/i.test(window.navigator.userAgent || "")) {
            setIos(true);
            setVisible(true);
        }

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const dismiss = () => {
        setVisible(false);
        localStorage.setItem(
            DISMISSED_KEY,
            String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
        );
    };

    const install = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            const choice = await deferred.userChoice;
            if (choice?.outcome === "accepted") {
                setVisible(false);
                setDeferred(null);
            } else {
                dismiss();
            }
        } catch (_) {
            dismiss();
        }
    };

    if (!visible) return null;
    // Only show the install affordance once the user is inside the app —
    // pre-auth views (landing/login/register) shouldn't push install yet.
    if (!user) return null;

    return (
        <div
            className="fixed left-1/2 -translate-x-1/2 z-[60] bottom-20 sm:bottom-6 w-[min(92vw,420px)]"
            data-testid="pwa-install-banner"
        >
            <div className="rounded-xl shadow-lg shadow-black/20 border border-orange-200 bg-white p-3 sm:p-4 flex items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center">
                    <Download className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-slate-900 text-sm">
                        Instala Emprenovus
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                        {ios
                            ? "Pulsa Compartir en Safari y elige “Agregar a inicio” para abrir Emprenovus como una app."
                            : "Agrégalo a tu pantalla de inicio para acceso rápido y modo offline."}
                    </p>
                    {!ios && (
                        <div className="mt-2 flex items-center gap-2">
                            <Button
                                size="sm"
                                onClick={install}
                                data-testid="pwa-install-accept"
                            >
                                <Download className="w-3.5 h-3.5 mr-1.5" />Instalar
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={dismiss}
                                data-testid="pwa-install-dismiss"
                            >
                                Ahora no
                            </Button>
                        </div>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Cerrar"
                    className="p-1 rounded hover:bg-slate-100 text-slate-400"
                    data-testid="pwa-install-close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
